import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAudience, AUDIENCES } from "@/contexts/AudienceContext";
import { Phone, ArrowLeft, AlertTriangle, Pill, Activity, Users, ShieldAlert, Baby, ArrowRight, BookOpen, Stethoscope, Heart, FlaskConical, TriangleAlert, Clock } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AudienceBadge() {
  const { audience, openSelector } = useAudience();
  const current = AUDIENCES.find(a => a.id === audience);
  return (
    <button
      onClick={openSelector}
      data-testid="change-audience"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-medium hover:bg-accent/20 transition-colors"
    >
      <span>{current?.icon}</span> Viewing as: {current?.label || 'Patient'}
      <span className="text-[10px] opacity-70">(change)</span>
    </button>
  );
}

export default function MedicineDetailPage() {
  const { slug } = useParams();
  const [med, setMed] = useState(null);
  const [loading, setLoading] = useState(true);
  const { audience } = useAudience();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/medicines/${slug}`)
      .then(res => { setMed(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!med) return <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4"><p className="text-muted-foreground">Medicine not found.</p><Link to="/medicines"><Button variant="outline">Back to Medicines</Button></Link></div>;

  const showStudentContent = audience === 'student' || audience === 'professional';
  const showCaregiverContent = audience === 'caregiver';
  const showPatientContent = audience === 'patient' || !audience;

  return (
    <div>
      <section className="py-12 sm:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/medicines" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" data-testid="back-to-medicines">
            <ArrowLeft className="w-4 h-4" /> Back to Medicines Guide
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{med.category}</span>
            <AudienceBadge />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-2" data-testid="medicine-title">{med.name}</h1>
          <p className="text-sm text-muted-foreground">Also known as: <strong className="text-foreground">{med.brand_names?.join(", ")}</strong></p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2 space-y-10">
            {/* Disclaimer */}
            <div className="flex items-start gap-2 p-4 rounded-lg border border-accent/30 bg-accent/5">
              <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Important:</strong> This is informational content only. Always consult Dr. Ambrish Singal or your psychiatrist before starting, stopping, or changing any medication.
              </p>
            </div>

            {/* What Is It */}
            <section>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold mb-3">What is {med.name}?</h2>
              <p className="text-base text-muted-foreground leading-relaxed">{med.what_is_it}</p>
            </section>

            {/* Mechanism */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">How It Works</h2>
              </div>
              <p className="text-base text-muted-foreground leading-relaxed">{med.mechanism}</p>
            </section>

            {/* STUDENT/PROFESSIONAL: Pharmacokinetics */}
            {showStudentContent && med.pharmacokinetics && (
              <section data-testid="pharmacokinetics-section" className="border-l-4 border-accent pl-5">
                <div className="flex items-center gap-2 mb-3">
                  <FlaskConical className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Pharmacokinetics</h2>
                  <span className="px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium">Detailed</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {Object.entries(med.pharmacokinetics).map(([key, val]) => (
                    <div key={key} className="p-3 rounded-lg bg-secondary/50">
                      <h4 className="font-heading font-medium text-xs uppercase tracking-wider text-primary mb-1">{key.replace(/_/g, ' ')}</h4>
                      <p className="text-sm text-muted-foreground">{val}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Uses */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Pill className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Uses</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {med.uses?.map((u, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-secondary text-sm text-secondary-foreground">{u}</span>
                ))}
              </div>
            </section>

            {/* HOW IT STARTS WORKING - Visible to ALL */}
            {med.onset_of_action && (
              <section data-testid="onset-section">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">How This Drug Starts Working</h2>
                </div>
                <Card className="border border-accent/20 bg-accent/5 mb-4">
                  <CardContent className="p-5">
                    <p className="text-base font-medium text-foreground">{med.onset_of_action.summary}</p>
                  </CardContent>
                </Card>
                {med.onset_of_action.week_by_week && (
                  <div className="space-y-3 mb-5">
                    <h3 className="font-heading font-semibold text-base">Week-by-Week Timeline</h3>
                    {med.onset_of_action.week_by_week.map((week, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                        <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{i+1}</span>
                        <p className="text-sm text-muted-foreground">{week}</p>
                      </div>
                    ))}
                  </div>
                )}
                {med.onset_of_action.what_changes_first && (
                  <div className="mb-4">
                    <h3 className="font-heading font-semibold text-base mb-2">What Changes First?</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{med.onset_of_action.what_changes_first}</p>
                  </div>
                )}
                {med.onset_of_action.why_it_takes_time && (
                  <div>
                    <h3 className="font-heading font-semibold text-base mb-2">Why Does It Take Time?</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{med.onset_of_action.why_it_takes_time}</p>
                  </div>
                )}
              </section>
            )}

            {/* DETAILED DOSAGE GUIDE - Visible to ALL */}
            {med.dosage_guide && (
              <section data-testid="dosage-guide-section">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Complete Dosage Guide</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-5">
                  {[
                    { key: "how_to_take", label: "How to Take", icon: "💊" },
                    { key: "starting", label: "Starting Dose", icon: "🟢" },
                    { key: "titration", label: "Dose Increases", icon: "📈" },
                    { key: "maximum", label: "Maximum Dose", icon: "⚠️" },
                    { key: "timing_tips", label: "When to Take", icon: "🕐" },
                    { key: "missed_dose", label: "If You Miss a Dose", icon: "❓" },
                    { key: "food_interactions", label: "Food & Drink", icon: "🍽️" },
                    { key: "duration", label: "How Long to Take", icon: "📅" },
                  ].map(({ key, label, icon }) => med.dosage_guide[key] && (
                    <Card key={key} className="border">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-base">{icon}</span>
                          <h4 className="font-heading font-medium text-sm text-primary">{label}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{med.dosage_guide[key]}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}

            {/* Quick Dosage Reference (original) */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Dosage by Age Group</h2>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {["adult", "child", "elderly"].map(key => (
                  <Card key={key} className="border">
                    <CardContent className="p-4">
                      <h4 className="font-heading font-medium text-sm uppercase tracking-wider text-primary mb-2">{key}</h4>
                      <p className="text-sm text-muted-foreground">{med.dosage?.[key]}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Special Populations - right after dosage */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Baby className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Special Populations</h2>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { key: "pregnancy", label: "Pregnancy" },
                  { key: "kidney", label: "Kidney Conditions" },
                  { key: "liver", label: "Liver Conditions" },
                ].map(({ key, label }) => (
                  <Card key={key} className="border">
                    <CardContent className="p-4">
                      <h4 className="font-heading font-medium text-sm text-primary mb-2">{label}</h4>
                      <p className="text-sm text-muted-foreground">{med.special_populations?.[key]}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Side Effects */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl sm:text-2xl font-semibold">Side Effects</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-heading font-medium text-sm mb-2">Common</h4>
                  <ul className="space-y-1.5">
                    {med.side_effects?.common?.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground shrink-0 mt-1.5" />{s}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-heading font-medium text-sm mb-2 text-destructive">Serious</h4>
                  <ul className="space-y-1.5">
                    {med.side_effects?.serious?.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <AlertTriangle className="w-3 h-3 text-destructive shrink-0 mt-1" />{s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* STUDENT/PROFESSIONAL: Drug Interactions */}
            {showStudentContent && med.drug_interactions && med.drug_interactions.length > 0 && (
              <section data-testid="interactions-section" className="border-l-4 border-accent pl-5">
                <div className="flex items-center gap-2 mb-3">
                  <TriangleAlert className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Drug Interactions</h2>
                  <span className="px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium">Clinical</span>
                </div>
                <div className="space-y-3">
                  {med.drug_interactions.map((int, i) => (
                    <div key={i} className="p-4 rounded-lg bg-secondary/50 border-l-3">
                      <div className="flex items-center gap-2 mb-1">
                        <strong className="text-sm">{int.drug}</strong>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          int.severity === 'Contraindicated' ? 'bg-destructive/10 text-destructive' :
                          int.severity?.includes('Major') ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>{int.severity}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{int.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* STUDENT/PROFESSIONAL: Monitoring & Clinical Pearls */}
            {showStudentContent && (
              <>
                {med.monitoring_parameters && (
                  <section data-testid="monitoring-section" className="border-l-4 border-primary pl-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Stethoscope className="w-5 h-5 text-primary" />
                      <h2 className="font-heading text-xl sm:text-2xl font-semibold">Monitoring Parameters</h2>
                    </div>
                    <ul className="space-y-2">
                      {med.monitoring_parameters.map((m, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />{m}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {med.clinical_pearls && (
                  <section data-testid="clinical-pearls-section" className="border-l-4 border-accent pl-5">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-5 h-5 text-accent" />
                      <h2 className="font-heading text-xl sm:text-2xl font-semibold">Clinical Pearls</h2>
                    </div>
                    <ul className="space-y-2">
                      {med.clinical_pearls.map((p, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="text-accent font-bold shrink-0">*</span>{p}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}

            {/* PATIENT: Patient Counseling */}
            {showPatientContent && med.patient_counseling && (
              <section data-testid="patient-counseling-section">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">What You Should Know</h2>
                </div>
                <div className="space-y-2">
                  {med.patient_counseling.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-secondary/50">
                      <span className="text-primary font-bold shrink-0">{i + 1}.</span>
                      <span className="text-sm text-muted-foreground">{tip}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* CAREGIVER: Caregiver Tips */}
            {showCaregiverContent && med.caregiver_tips && (
              <section data-testid="caregiver-tips-section">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="w-5 h-5 text-accent" />
                  <h2 className="font-heading text-xl sm:text-2xl font-semibold">Caregiver Guide</h2>
                  <span className="px-2 py-0.5 rounded text-xs bg-accent/10 text-accent font-medium">For Family</span>
                </div>
                <div className="space-y-2">
                  {med.caregiver_tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20">
                      <span className="text-accent font-bold shrink-0">{i + 1}.</span>
                      <span className="text-sm text-muted-foreground">{tip}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Overdose */}
            <section>
              <Card className="border border-destructive/20 bg-destructive/5">
                <CardContent className="p-5">
                  <h2 className="font-heading text-lg font-semibold mb-2">Overdose Risks</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{med.overdose_risks}</p>
                </CardContent>
              </Card>
            </section>

            {/* Discontinuation */}
            <section>
              <Card className="border border-primary/20 bg-primary/5">
                <CardContent className="p-5">
                  <h2 className="font-heading text-lg font-semibold mb-2">Safe Discontinuation</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{med.discontinuation}</p>
                </CardContent>
              </Card>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <Card className="border sticky top-24">
              <CardContent className="p-6 space-y-4">
                <h3 className="font-heading font-semibold text-lg">Consult About This Medication</h3>
                <p className="text-sm text-muted-foreground">
                  Have questions about {med.name}? Dr. Ambrish Singal can help with dosage, side effects, and alternatives.
                </p>
                <a href="tel:+919915096633" className="block" data-testid="med-sidebar-call">
                  <Button className="w-full gap-2 rounded-full">
                    <Phone className="w-4 h-4" /> Call +91-99150 96633
                  </Button>
                </a>
                <Link to="/contact" className="block" data-testid="med-sidebar-book">
                  <Button variant="outline" className="w-full gap-2 rounded-full">
                    Book Consultation <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}
